import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Star, Heart, Compass, Mic, ShoppingBag, User, Tag, SlidersHorizontal, ChevronRight, MapPin, Loader2, Coffee, Pizza, Beef, Soup, Salad, LayoutGrid } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { VoicePanel } from "../components/VoicePanel";
import { AISearchBar } from "../components/AISearchBar";

// Fix leaflet icons
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });

const DEFAULT_CENTER = [40.6336, 22.9430];
const DEFAULT_ZOOM   = 14;

const CATEGORIES = [
    { id: "all",    label: "Όλα",     icon: LayoutGrid },
    { id: "burger", label: "Burger",  icon: Beef },
    { id: "pizza",  label: "Pizza",   icon: Pizza },
    { id: "asian",  label: "Asian",   icon: Soup },
    { id: "coffee", label: "Coffee",  icon: Coffee },
    { id: "street", label: "Street",  icon: Salad },
];

// Cluster two stores into one disc when within ~0.012 lat/lng
function clusterStores(stores) {
    const clusters = [];
    const threshold = 0.005; // tighter clustering
    stores.forEach((s) => {
        const c = clusters.find((cl) =>
            Math.abs(cl.center[0] - s.location.lat) < threshold &&
            Math.abs(cl.center[1] - s.location.lng) < threshold
        );
        if (c) {
            c.stores.push(s);
            const lat = c.stores.reduce((acc, x) => acc + x.location.lat, 0) / c.stores.length;
            const lng = c.stores.reduce((acc, x) => acc + x.location.lng, 0) / c.stores.length;
            c.center = [lat, lng];
        } else {
            clusters.push({ stores: [s], center: [s.location.lat, s.location.lng] });
        }
    });
    return clusters;
}

const buildClusterIcon = (cluster) => {
    const count = cluster.stores.length;
    const isHot = cluster.stores.some((s) => s.is_hot);
    const single = count === 1;
    if (single) {
        const s = cluster.stores[0];
        return L.divIcon({
            className: "store-marker",
            html: `<div class="store-marker-wrap pulse">
                     <div class="store-marker-ring"></div>
                     <div class="store-marker-image">
                       <img src="${s.image_url || s.logo_url || ''}" alt="${s.name}" />
                       ${isHot ? '<div class="hot">🔥 HOT</div>' : ''}
                     </div>
                     <div class="store-marker-pin"></div>
                   </div>`,
            iconSize: [88, 100],
            iconAnchor: [44, 92],
        });
    }
    return L.divIcon({
        className: "store-marker",
        html: `<div class="store-marker-wrap">
                 <div class="store-marker-ring"></div>
                 <div class="store-marker-disc">
                   <span class="count">${count}</span>
                   <span class="label">PLACES</span>
                   ${isHot ? '<div class="hot">🔥 HOT</div>' : ''}
                 </div>
                 <div class="store-marker-pin"></div>
               </div>`,
        iconSize: [88, 100],
        iconAnchor: [44, 92],
    });
};

const RecenterButton = ({ center }) => {
    const map = useMap();
    return (
        <button
            data-testid="map-recenter-btn"
            onClick={() => map.flyTo(center, DEFAULT_ZOOM, { duration: 0.8 })}
            className="absolute z-[400] right-3 bottom-72 h-10 w-10 grid place-items-center rounded-full glass-gold text-white/80 hover:text-[var(--gold-light)]"
            aria-label="recenter"
        >
            <MapPin size={16} />
        </button>
    );
};

export const MapHome = () => {
    const navigate = useNavigate();
    const { count } = useCart();
    const { user } = useAuth();
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState("all");
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [voiceOpen, setVoiceOpen] = useState(false);
    const [favs, setFavs] = useState(() => JSON.parse(localStorage.getItem("favs") || "[]"));

    useEffect(() => {
        api.get("/stores").then(({ data }) => {
            setStores(data || []);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const filteredStores = useMemo(() => {
        if (activeCategory === "all") return stores;
        return stores.filter((s) => (s.categories || []).includes(activeCategory) || s.cuisine === activeCategory);
    }, [stores, activeCategory]);

    const clusters = useMemo(() => clusterStores(filteredStores), [filteredStores]);

    const center = useMemo(() => {
        if (filteredStores.length > 0) {
            const lat = filteredStores.reduce((s, x) => s + x.location.lat, 0) / filteredStores.length;
            const lng = filteredStores.reduce((s, x) => s + x.location.lng, 0) / filteredStores.length;
            return [lat, lng];
        }
        return DEFAULT_CENTER;
    }, [filteredStores]);

    const onClusterClick = (cluster) => {
        if (cluster.stores.length === 1) {
            setSelectedCluster(cluster);
        } else {
            setSelectedCluster(cluster);
        }
    };

    const toggleFav = (storeId) => {
        const next = favs.includes(storeId) ? favs.filter((x) => x !== storeId) : [...favs, storeId];
        setFavs(next);
        localStorage.setItem("favs", JSON.stringify(next));
    };

    const featuredStore = selectedCluster?.stores?.[0] || filteredStores[0];

    return (
        <div data-testid="map-home" className="fixed inset-0 cyber-bg text-white overflow-hidden">
            {/* Vignette + depth overlay */}
            <div className="pointer-events-none absolute inset-0 z-[200]" style={{
                background: "radial-gradient(ellipse at center, transparent 30%, rgba(11,11,15,0.6) 75%, rgba(11,11,15,0.95) 100%)",
            }} />
            {/* Activity dots — fake live pulses near busy areas */}
            <div className="pointer-events-none absolute inset-0 z-[150]">
                <div className="activity-dot" style={{ left: "32%", top: "55%" }} />
                <div className="activity-dot" style={{ left: "58%", top: "42%", animationDelay: "1.2s" }} />
                <div className="activity-dot" style={{ left: "44%", top: "68%", animationDelay: "2.4s" }} />
            </div>
            {/* HEADER */}
            <header className="absolute top-0 left-0 right-0 z-[500] px-5 pt-3">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-[9px] tracking-[0.32em] text-white/50 font-mono uppercase">THESSALONIKI ▾</p>
                        <h1 className="font-heading tracking-tight text-3xl mt-0.5" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                            <span className="text-white">Thess</span>
                            <span className="gold-text">·</span>
                            <span className="text-white">Eats</span>
                        </h1>
                        <p className="text-[10px] text-white/50 mt-0.5 italic">Ανακάλυψε. Παράγγειλε. Απόλαυσε.</p>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                        <button data-testid="hdr-bell" className="relative h-10 w-10 grid place-items-center rounded-full glass-gold">
                            <Bell size={16} className="text-[var(--gold-light)]" />
                            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[var(--gold)] shadow-[0_0_8px_var(--gold)]" />
                        </button>
                    </div>
                </div>

                {/* AI Search Bar (text + push-to-talk) */}
                <div className="mt-3">
                    <AISearchBar />
                </div>

                {/* Category pills */}
                <div className="flex gap-2 overflow-x-auto scrollbar-hide mt-3 -mx-5 px-5 pb-1">
                    {CATEGORIES.map((c) => {
                        const Icon = c.icon;
                        const on = activeCategory === c.id;
                        return (
                            <button
                                key={c.id}
                                data-testid={`cat-${c.id}`}
                                onClick={() => setActiveCategory(c.id)}
                                className={`pill-chip ${on ? "active" : ""}`}
                            >
                                <Icon size={13} strokeWidth={2.2} />
                                <span>{c.label}</span>
                            </button>
                        );
                    })}
                </div>
            </header>

            {/* MAP */}
            <MapContainer
                center={center}
                zoom={DEFAULT_ZOOM}
                zoomControl={false}
                attributionControl={false}
                style={{ height: "100%", width: "100%" }}
                className="z-0"
                key={activeCategory}
            >
                <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                {clusters.map((cl, i) => (
                    <Marker
                        key={i}
                        position={cl.center}
                        icon={buildClusterIcon(cl)}
                        eventHandlers={{ click: () => onClusterClick(cl) }}
                    />
                ))}
                <RecenterButton center={center} />
            </MapContainer>

            {/* RIGHT-SIDE FLOATING BUTTONS */}
            <div className="absolute right-3 top-44 z-[450] flex flex-col gap-2.5">
                <button
                    data-testid="side-favorites"
                    onClick={() => navigate("/orders")}
                    className="h-14 w-14 grid place-items-center glass-gold rounded-2xl text-white/80 hover:text-[var(--gold-light)] flex-col"
                >
                    <Star size={16} className="text-[var(--gold-light)]" />
                    <span className="text-[8px] font-mono uppercase tracking-widest mt-1">Favorites</span>
                </button>
                <button
                    data-testid="side-offers"
                    onClick={() => navigate("/offers")}
                    className="h-14 w-14 grid place-items-center glass-gold rounded-2xl text-white/80 hover:text-[var(--gold-light)] flex-col"
                >
                    <Tag size={16} className="text-[var(--gold-light)]" />
                    <span className="text-[8px] font-mono uppercase tracking-widest mt-1">Offers</span>
                </button>
                <button
                    data-testid="side-filters"
                    className="h-14 w-14 grid place-items-center glass-gold rounded-2xl text-white/80 hover:text-[var(--gold-light)] flex-col"
                >
                    <SlidersHorizontal size={16} className="text-[var(--gold-light)]" />
                    <span className="text-[8px] font-mono uppercase tracking-widest mt-1">Filters</span>
                </button>
            </div>

            {/* FLOATING STORE CARD */}
            {featuredStore && !loading && (
                <div className="absolute left-3 right-3 bottom-24 z-[450]" data-testid="store-card-wrap">
                    <article className="glass-gold rounded-2xl overflow-hidden flex shadow-[0_10px_40px_rgba(0,0,0,0.7)]">
                        <img src={featuredStore.image_url || featuredStore.logo_url} alt={featuredStore.name} className="w-28 h-32 object-cover shrink-0" />
                        <div className="flex-1 p-3 min-w-0 relative">
                            {featuredStore.is_hot && (
                                <span className="inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-widest border border-[var(--gold)] text-[var(--gold-light)] px-2 py-0.5 rounded-full">
                                    <Star size={9} fill="currentColor" /> Top Rated
                                </span>
                            )}
                            <button
                                data-testid={`fav-${featuredStore.id}`}
                                onClick={() => toggleFav(featuredStore.id)}
                                className="absolute top-3 right-3 h-7 w-7 grid place-items-center rounded-full hover:bg-white/10"
                            >
                                <Heart size={14} fill={favs.includes(featuredStore.id) ? "var(--gold-light)" : "none"} className={favs.includes(featuredStore.id) ? "text-[var(--gold-light)]" : "text-white/60"} />
                            </button>
                            <h3 className="font-heading text-lg leading-tight mt-1.5 truncate" style={{ fontFamily: '"Cormorant Garamond", serif' }}>{featuredStore.name}</h3>
                            <p className="text-[10px] text-white/50 capitalize truncate">{featuredStore.cuisine} · {featuredStore.tagline.split('·')[0]}</p>
                            <div className="flex items-center gap-3 mt-1.5 text-[10px]">
                                <span className="flex items-center gap-1 text-[var(--gold-light)]">
                                    <Star size={10} fill="currentColor" />
                                    <span className="font-bold">{featuredStore.rating.toFixed(1)}</span>
                                    <span className="text-white/40">({featuredStore.rating_count})</span>
                                </span>
                                <span className="flex items-center gap-1 text-white/60">
                                    <MapPin size={10} />
                                    {featuredStore.delivery_minutes}'
                                </span>
                            </div>
                            <p className="text-[9px] text-white/40 mt-1">
                                <span className="text-emerald-400">Ανοιχτό</span> · Delivery €{featuredStore.delivery_fee.toFixed(2)}
                            </p>
                        </div>
                    </article>
                    {/* CTA */}
                    <button
                        data-testid="cta-order"
                        onClick={() => navigate(`/store/${featuredStore.slug}`)}
                        className="gold-cta mt-3 w-full h-14 rounded-2xl flex items-center justify-between px-5 font-heading uppercase tracking-wider text-sm font-black active:scale-[0.98] transition"
                    >
                        <span className="flex items-center gap-2">
                            <ShoppingBag size={18} strokeWidth={2.5} />
                            <span className="flex flex-col items-start leading-tight">
                                <span>Παραγγελία</span>
                                <span className="text-[9px] font-normal opacity-80">Παράλαβε ή delivery</span>
                            </span>
                        </span>
                        <ChevronRight size={20} strokeWidth={3} />
                    </button>
                </div>
            )}

            {loading && (
                <div className="absolute inset-0 grid place-items-center text-white/50 z-[400]">
                    <Loader2 className="animate-spin" size={28} />
                </div>
            )}

            {/* BOTTOM NAV with center voice button */}
            <nav className="absolute bottom-0 left-0 right-0 z-[460] glass-gold border-t border-[var(--gold)]/20">
                <div className="flex justify-around items-end pb-[env(safe-area-inset-bottom)] py-2 px-2 relative">
                    <NavTab id="explore" label="Εξερεύνησε" icon={Compass} active />
                    <NavTab id="favorites" label="Αγαπημένα" icon={Heart} onClick={() => navigate("/offers")} />

                    {/* CENTER VOICE BUTTON */}
                    <div className="relative -mt-7">
                        <button
                            onClick={() => setVoiceOpen(true)}
                            data-testid="bottom-voice"
                            aria-label="Voice order"
                            className="voice-center-btn h-16 w-16 grid place-items-center rounded-full active:scale-95 transition"
                        >
                            <Mic size={26} strokeWidth={2.5} className="text-[#0B0B0F]" />
                        </button>
                        <p className="absolute bottom-[-18px] left-1/2 -translate-x-1/2 text-[8px] font-mono uppercase tracking-widest text-[var(--gold-light)] whitespace-nowrap">VOICE</p>
                    </div>

                    <NavTab id="orders" label="Παραγγελίες" icon={ShoppingBag} count={count} onClick={() => navigate("/orders")} />
                    <NavTab id="profile" label="Προφίλ" icon={User} onClick={() => navigate(user ? "/orders" : "/login")} />
                </div>
            </nav>

            <VoicePanel
                open={voiceOpen}
                onClose={() => setVoiceOpen(false)}
                storeSlug={featuredStore?.slug || "grill-bros"}
                storeName={featuredStore?.name || "ThessFood"}
            />
        </div>
    );
};

const NavTab = ({ label, icon: Icon, active, onClick, count }) => (
    <button
        data-testid={`nav-${label}`}
        onClick={onClick}
        className={`flex-1 max-w-[80px] flex flex-col items-center justify-center gap-0.5 h-12 rounded-xl transition active:scale-95 ${active ? "text-[var(--gold-light)]" : "text-white/55 hover:text-white"}`}
    >
        <div className="relative">
            <Icon size={18} strokeWidth={active ? 2.5 : 2} />
            {count > 0 && (
                <span className="absolute -top-1.5 -right-2.5 min-w-[14px] h-3.5 px-1 grid place-items-center rounded-full bg-[var(--gold)] text-[#0B0B0F] text-[8px] font-mono font-bold">
                    {count}
                </span>
            )}
        </div>
        <span className="text-[8px] font-mono uppercase tracking-[0.15em] font-bold">{label}</span>
    </button>
);
